import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack } from '@mui/material';
import { prejudiceBaseDefaultValues } from '@norma/shared/constants/prejudice/defaultValues';
import { prejudicesData } from '@norma/shared/constants/prejudices';
import {
  PrejudiceFormCalendrierValeur,
  PrejudiceType,
} from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import { sortBy } from 'lodash';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ComputedPropsForm } from 'src/components/forms/ComputedPropsForm';
import {
  getDefaultMinAndMaxDate,
  validationSchemaNumeroPieces,
  validationSchemaPrejudiceBase,
} from 'src/constants/prejudice/validation';
import { dateString } from 'src/helpers/yup';
import * as yup from 'yup';
import { ExpandableTextFieldForm } from '../../forms/ExpandableTextFieldForm';
import { PrejudiceContainer } from '../PrejudiceContainer';
import { Chiffrage } from '../prejudiceFormComponents/Chiffrage';
import { TableValeurs } from '../prejudiceFormComponents/TableValeurs';
import { TotalDeficit } from '../prejudiceFormComponents/TotalDeficit';
import { PrejudiceFormProps } from '../PrejudiceFormProps';
import { SavePrejudiceButton } from '../SavePrejudiceButton';

export const validationSchemaCalendrierValeur = ({
  victime,
  noRevalorisation,
  prejudiceType,
  procedure,
}: {
  victime: Victime;
  noRevalorisation: boolean | undefined;
  prejudiceType: PrejudiceType;
  procedure: Procedure;
}): yup.ObjectSchema<PrejudiceFormCalendrierValeur> => {
  const { minDate, maxDate } = getDefaultMinAndMaxDate({
    procedure,
    victime,
    prejudiceType,
  });
  return validationSchemaPrejudiceBase.shape({
    rows: yup
      .array()
      .defined()
      .of(
        yup.object({
          numerosPieces: validationSchemaNumeroPieces,
          date: dateString()
            .nullable()
            .required()
            .minDate(undefined, minDate.message, minDate.date)
            .maxDate(undefined, maxDate.message, maxDate.date),
          montant: yup.number().required().min(0),
          intitule: yup.string().required(),
          montantsDejaRevalorises: yup
            .boolean()
            .when([], () =>
              noRevalorisation
                ? yup.boolean().optional()
                : yup.boolean().required(),
            ),
        }),
      ),
    total: yup.number().required().min(0),
  });
};

export const calendrierValeurDefaultValues = ({
  values,
}: {
  values: PrejudiceFormCalendrierValeur | undefined;
}): PrejudiceFormCalendrierValeur => ({
  ...prejudiceBaseDefaultValues({
    values,
  }),
  notes: values?.notes || '',
  rows: sortBy(values?.rows || [], (row) => row.date),
  total: values?.total || 0,
});

type Props = PrejudiceFormProps<PrejudiceFormCalendrierValeur>;

export const FormCalendrierValeur: React.FC<Props> = ({
  procedure,
  victime,
  values,
  monetaryErosions,
  displayTotalPartResponsabilite,
  partResponsabilite,
  allNumerosPieces,
  ...props
}) => {
  const noRevalorisation =
    prejudicesData[props.prejudiceType || 'DEPENSE_SANTE_FUTURES']
      .noRevalorisation;
  const useFormReturn = useForm<PrejudiceFormCalendrierValeur>({
    defaultValues: calendrierValeurDefaultValues({
      values,
    }),
    resolver: yupResolver(
      validationSchemaCalendrierValeur({
        victime,
        noRevalorisation,
        prejudiceType: props.prejudiceType,
        procedure,
      }),
    ),
  });
  const { control } = useMemo(() => useFormReturn, [useFormReturn]);
  const { t } = useTranslation();

  return (
    <PrejudiceContainer<PrejudiceFormCalendrierValeur>
      {...props}
      victime={victime}
      procedure={procedure}
      monetaryErosions={monetaryErosions}
      {...useFormReturn}
      renderPrejudice={() => (
        <Stack direction="column" spacing={4} flex={1}>
          <ExpandableTextFieldForm
            control={control}
            name="notes"
            label={t('prejudice.fields.notes.label')}
            placeholder={t('prejudice.fields.notes.placeholder') || ''}
            dialogTitle={t('prejudice.fields.notes.label')}
            InputLabelProps={{ shrink: true }}
            multiline
            maxRows={10}
            fullWidth
          />
          <TableValeurs
            control={control}
            dateLiquidation={procedure.dateLiquidation}
            monetaryErosions={monetaryErosions}
            noRevalorisation={noRevalorisation}
            procedure={procedure}
            victime={victime}
            prejudiceType={props.prejudiceType}
            allNumerosPieces={allNumerosPieces}
          />
          {displayTotalPartResponsabilite && partResponsabilite && (
            <ComputedPropsForm
              control={control}
              watchFields={['total']}
              compute={([total]) => ({
                props: {
                  totalPartResponsabilite: Number(total) * partResponsabilite,
                  total: Number(total),
                },
              })}
              render={({ totalPartResponsabilite, total }) => (
                <Stack alignSelf="end" maxWidth="400px">
                  <TotalDeficit
                    totalPartResponsabilite={totalPartResponsabilite}
                    totalMontantTotal={total}
                    displayPartResponsabilite={displayTotalPartResponsabilite}
                    partResponsabilite={partResponsabilite}
                  />
                </Stack>
              )}
            />
          )}
          <Grid container>
            <Grid item xs={4}>
              <Chiffrage control={control} />
            </Grid>
            <Grid
              item
              xs={4}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <SavePrejudiceButton victime={victime} />
            </Grid>
            <Grid item xs={4} />
          </Grid>
        </Stack>
      )}
    />
  );
};
